'use client';
import { useCallback, useEffect, useState } from 'react';
import useHuddle01 from './useHuddle01';
import type { TPermissions } from 'types/dist/common.types';

export const useLocalPeer = <T = unknown>(props?: {
  onMetadataUpdated?: (metadata: T) => void;
  onPermissionsUpdated?: (permissions: TPermissions) => void;
  onRoleUpdated?: (role: string) => void;
}) => {
  const { huddleClient } = useHuddle01();

  const localPeer = huddleClient.localPeer;

  const peerId = localPeer.peerId;

  const [permissions, setPermissions] = useState<TPermissions>(
    localPeer.permissions,
  );

  const [role, setRole] = useState<string | null>(localPeer.role);

  const [metadata, setMetadata] = useState<T | null>(
    localPeer.getMetadata<T>() ?? null,
  );

  const handleUpdateMetadata = useCallback(() => {
    const newMetadata = localPeer.getMetadata<T>();

    if (props?.onMetadataUpdated) props.onMetadataUpdated(newMetadata);

    setMetadata(newMetadata);
  }, [props?.onMetadataUpdated]);

  const handlePermissionsUpdated = useCallback(
    (data: {
      permissions: TPermissions;
      role?: string;
    }) => {
      if (props?.onPermissionsUpdated) {
        props.onPermissionsUpdated(data.permissions);
      }

      if (data.role) {
        if (props?.onRoleUpdated) props.onRoleUpdated(data.role);
        setRole(data.role);
      }

      setPermissions(data.permissions);
    },
    [props?.onPermissionsUpdated, props?.onRoleUpdated],
  );

  const handleRoleUpdated = useCallback(
    (data: { role: string }) => {
      if (props?.onRoleUpdated) props.onRoleUpdated(data.role);

      setRole(data.role);
    },
    [props?.onRoleUpdated],
  );

  useEffect(() => {
    localPeer.on('metadata-updated', handleUpdateMetadata);

    localPeer.on('permissions-updated', handlePermissionsUpdated);

    localPeer.on('role-updated', handleRoleUpdated);

    return () => {
      localPeer.off('metadata-updated', handleUpdateMetadata);

      localPeer.off('permissions-updated', handlePermissionsUpdated);

      localPeer.off('role-updated', handleRoleUpdated);
    };
  }, []);

  const updateMetadata = localPeer.updateMetadata<T>;

  const updateRole = localPeer.updateRole;

  return {
    peerId,
    role,
    permissions,
    metadata,
    updateMetadata,
    updateRole,
  };
};
